/**
 * Pure search and grouping rules of the staff FAQ
 * (openspec/changes/staff-faq/spec.md, B1, B2, B3, B6). No I/O, no DOM.
 *
 * Search runs in the browser over every visible entry (spec "Design"): the
 * FAQ is small, and a pure function is instant while typing and testable
 * without a database.
 */

/** Words ignored in a query: they appear in almost every question. */
const STOPWORDS = new Set([
    'que', 'como', 'cuando', 'cuanto', 'donde', 'quien', 'cual',
    'los', 'las', 'del', 'por', 'para', 'con', 'sin', 'una', 'uno', 'unos', 'unas',
    'hay', 'hago', 'puedo', 'tengo', 'debo', 'algo', 'alguien', 'mis', 'tus', 'sus',
    'esta', 'este', 'eso', 'esa', 'ese', 'mas', 'muy', 'pero', 'porque',
]);

/** Score of a query word found in pregunta or otras formas vs. only in the answer text. */
const PESO_PREGUNTA = 3;
const PESO_RESPUESTA = 1;

/** Minimum trimmed length of a query worth recording as a failed search (B3). */
const MIN_CONSULTA_REGISTRABLE = 3;

/**
 * Lowercase, accents removed (ñ kept), punctuation to spaces, spaces collapsed.
 * @param {string | null | undefined} texto
 * @returns {string}
 */
export function normalizar(texto) {
    return String(texto ?? '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/n\u0303/g, 'ñ')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9ñ]+/g, ' ')
        .trim();
}

/** Drops a plural trailing `s` so "propinas" and "propina" meet. */
function singular(palabra) {
    return palabra.length > 3 && palabra.endsWith('s') ? palabra.slice(0, -1) : palabra;
}

/** @param {string | null | undefined} texto */
function palabrasDe(texto) {
    const n = normalizar(texto);
    return n ? n.split(' ').map(singular) : [];
}

/**
 * The words of a query that take part in the search: 3+ letters, no stopwords.
 * @param {string} consulta
 * @returns {string[]}
 */
export function palabrasConsulta(consulta) {
    return normalizar(consulta)
        .split(' ')
        .filter((p) => p.length >= 3 && !STOPWORDS.has(p))
        .map(singular)
        // Checked again after dropping the plural: "cuántos" must not become "cuanto" and match.
        .filter((p) => !STOPWORDS.has(p));
}

/** Does any word of `palabras` start with `consulta`? */
const contiene = (palabras, consulta) => palabras.some((p) => p.startsWith(consulta));

/**
 * Entries matching `consulta`, best first (B2). An entry with score 0 is not
 * a result; an empty or stopword-only query returns [].
 * @template {{ pregunta: string, en_corto: string, ojo?: string | null, pasos?: string[], otras_formas?: string[] }} T
 * @param {T[]} entradas
 * @param {string} consulta
 * @returns {T[]}
 */
export function buscar(entradas, consulta) {
    const palabras = palabrasConsulta(consulta);
    if (palabras.length === 0) return [];

    return entradas
        .map((entrada) => {
            const enPregunta = [...palabrasDe(entrada.pregunta), ...(entrada.otras_formas ?? []).flatMap(palabrasDe)];
            const enRespuesta = [
                ...palabrasDe(entrada.en_corto),
                ...palabrasDe(entrada.ojo),
                ...(entrada.pasos ?? []).flatMap(palabrasDe),
            ];
            let puntaje = 0;
            for (const palabra of palabras) {
                if (contiene(enPregunta, palabra)) puntaje += PESO_PREGUNTA;
                else if (contiene(enRespuesta, palabra)) puntaje += PESO_RESPUESTA;
            }
            return { entrada, puntaje };
        })
        .filter(({ puntaje }) => puntaje > 0)
        .sort((a, b) => b.puntaje - a.puntaje || a.entrada.pregunta.localeCompare(b.entrada.pregunta, 'es'))
        .map(({ entrada }) => entrada);
}

/**
 * Entries grouped by category (B1): categories alphabetically, entries by pregunta.
 * @template {{ categoria: string, pregunta: string }} T
 * @param {T[]} entradas
 * @returns {Array<{ categoria: string, entradas: T[] }>}
 */
export function agruparPorCategoria(entradas) {
    /** @type {Map<string, T[]>} */
    const grupos = new Map();
    for (const entrada of entradas) {
        if (!grupos.has(entrada.categoria)) grupos.set(entrada.categoria, []);
        grupos.get(entrada.categoria).push(entrada);
    }
    return [...grupos.entries()]
        .sort(([a], [b]) => a.localeCompare(b, 'es'))
        .map(([categoria, lista]) => ({
            categoria,
            entradas: [...lista].sort((a, b) => a.pregunta.localeCompare(b.pregunta, 'es')),
        }));
}

/**
 * Key that groups the same failed search typed differently (B3 dedupe, B6 groups).
 * @param {string} consulta
 * @returns {string}
 */
export function claveBusqueda(consulta) {
    return normalizar(consulta);
}

/**
 * Is `consulta` long enough to be recorded as a failed search (B3)?
 * @param {string} consulta
 * @returns {boolean}
 */
export function esConsultaRegistrable(consulta) {
    return String(consulta ?? '').trim().length >= MIN_CONSULTA_REGISTRABLE;
}

/**
 * Pending search-log rows grouped by `claveBusqueda` (B6), newest group first.
 * `consulta` is the text of the most recent row of the group; `consultas` are
 * all its raw texts (sent by "Marcar revisada" when the list was cut).
 * @param {Array<{ id: string, consulta: string, tipo: string, created_at: string }>} filas
 * @returns {Array<{ clave: string, consulta: string, consultas: string[], tipos: string[], cantidad: number, ultima: string, ids: string[] }>}
 */
export function agruparBusquedas(filas) {
    /** @type {Map<string, { clave: string, consulta: string, consultas: Set<string>, tipos: Set<string>, cantidad: number, ultima: string, ids: string[] }>} */
    const grupos = new Map();
    for (const fila of filas) {
        const clave = claveBusqueda(fila.consulta);
        const grupo = grupos.get(clave) ?? { clave, consulta: fila.consulta, consultas: new Set(), tipos: new Set(), cantidad: 0, ultima: fila.created_at, ids: [] };
        grupo.consultas.add(fila.consulta);
        grupo.tipos.add(fila.tipo);
        grupo.cantidad += 1;
        grupo.ids.push(fila.id);
        if (fila.created_at >= grupo.ultima) {
            grupo.ultima = fila.created_at;
            grupo.consulta = fila.consulta;
        }
        grupos.set(clave, grupo);
    }
    return [...grupos.values()]
        .sort((a, b) => (a.ultima < b.ultima ? 1 : a.ultima > b.ultima ? -1 : 0))
        .map((g) => ({ ...g, consultas: [...g.consultas].sort(), tipos: [...g.tipos].sort() }));
}
